import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Grid, List, Eye, Heart, Star, Tag, Trash2, TrendingUp, TrendingDown, X, Package, Wallet, ArrowLeftRight, ChevronDown, Check, Image as ImageIcon, Box, ChevronRight, Info, CheckCircle, AlertCircle } from 'lucide-react';
import CustomIcon from '../components/CustomIcon';
import ModelPreview from '../components/ModelPreview';
import ModelViewer from '../components/ModelViewer';
import UnifiedPieceCard from '../components/UnifiedPieceCard';
import FavoritePieceCard from '../components/FavoritePieceCard';
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import RequestTradeModal from '../components/RequestTradeModal';
import { useWallet } from '../contexts/WalletContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { CLOSET } from '../data/closet';
import { homeData } from '../data/homeData';
import { useCapsuleFavorites } from '../hooks/useCapsuleFavorites';
import { useSupabaseFavorites } from '../hooks/useSupabaseFavorites';
import { supabase } from '../lib/supabase';
import { fetchFavoritePieces, normalizeWallet } from '../lib/favorites';
import { normalizePieces } from '../utils/pieceNormalizer.js';
import { useWalletFavorites } from '../hooks/useWalletFavorites';
import { useWalletAction } from '../hooks/useWalletAction';
import WalletConnectionModal from '../components/WalletConnectionModal';
import WalletRequired from '../components/WalletRequired';
import { 
  getPopularPieces, 
  getNewPieces, 
  getTrendingPieces, 
  formatPrice, 
  getCategoryDisplayName 
} from '../utils/pieceUtils';
import {
  createListing,
  purchaseListing,
  cancelListing,
  getActiveListing,
  canListPiece,
  canPurchaseListing,
  subscribeToListing,
  subscribeToPiece,
} from '../utils/resaleMarketplace';
import { createOffer } from '../utils/offersContract';
import { getCapsuleForPiece } from '../utils/supabaseDatabase';
import { createGroupedPieceCards, isGroupedCard, getAvailablePieceFromGroup } from '../utils/pieceGrouping';
import GroupedPiecesModal from '../components/GroupedPiecesModal';
import { trackPieceView } from '../utils/supabaseViewTracking';
import VerifiedBadge from '../components/VerifiedBadge';

const Favorites = () => {
  const navigate = useNavigate();
  const { isConnected, balance, publicKey, connectWallet, wallet } = useWallet();
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  const { toggleFavorite, isFavorited, getFavoriteIds, loading: favoritesLoading, refreshFavorites } = useSupabaseFavorites(true); // Skip event reload on Favorites page
  const { toggleCapsuleFavorite, isCapsuleFavorited } = useCapsuleFavorites();
  const { toggleWalletFavorite, isWalletFavorited } = useWalletFavorites();
  const { withWalletCheck, showWalletModal, walletModalMessage, closeWalletModal } = useWalletAction();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [activeTab, setActiveTab] = useState('pieces'); // New tab state
  const [favoriteItems, setFavoriteItems] = useState(new Set()); // Track which items are favorited
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  const [favoriteWallets, setFavoriteWallets] = useState(new Set()); // Track which wallets are favorited
  const [favoritePiecesData, setFavoritePiecesData] = useState([]); // Store actual piece data for favorites
  const [favoriteCapsulesData, setFavoriteCapsulesData] = useState([]); // Store actual capsule data for favorites
  const [favoriteWalletsData, setFavoriteWalletsData] = useState([]); // Store actual wallet data for favorites
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, item: null });
  const [groupedPiecesModal, setGroupedPiecesModal] = useState({ isOpen: false, groupedCard: null });
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [threeDStatus, setThreeDStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [threeDError, setThreeDError] = useState(null);
  const [currentPieceGroupIds, setCurrentPieceGroupIds] = useState(null); // Store grouped piece IDs for current piece
  const [buyModal, setBuyModal] = useState({ isOpen: false, item: null });
  const [offerModal, setOfferModal] = useState({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
  const [requestTradeModal, setRequestTradeModal] = useState({ isOpen: false, item: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [localFavoritedPieces, setLocalFavoritedPieces] = useState(new Set()); // Track favorited state locally
  const [localFavoritedCapsules, setLocalFavoritedCapsules] = useState(new Set()); // Track favorited capsules locally
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null); // Track capsule info for current piece
  const [activeListing, setActiveListing] = useState(null);
  const [listingLoading, setListingLoading] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [showPurchaseConfirmModal, setShowPurchaseConfirmModal] = useState(false);
  const [listPrice, setListPrice] = useState('');
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  
  const lastTrackedPiece = useRef(null);

  // Load favorite pieces from Supabase using the correct query pattern
  const loadFavoritePieces = async () => {
    if (!isConnected || !publicKey) {
      setFavoritePiecesData([]);
      setFavoriteItems(new Set());
      return;
    }

    try {
      const userWallet = publicKey.toString().toLowerCase();
      
      // First, get the favorite item IDs
      let { data: favorites, error: favoritesError } = await supabase
        .from('favorites')
        .select('item_id, created_at')
        .eq('user_wallet', userWallet)
        .eq('item_type', 'piece')
        .order('created_at', { ascending: false });

      if (favoritesError) {
        console.error('Error fetching favorites:', favoritesError);
        setFavoritePiecesData([]);
        setFavoriteItems(new Set());
        return;
      }
      
      // Clean up duplicate favorites with different ID formats (e.g., "12230" and "supabase_12230")
      if (favorites && favorites.length > 0) {
        const seenIds = new Set();
        const duplicatesToDelete = [];
        
        favorites.forEach(fav => {
          const id = String(fav.item_id);
          // Extract numeric part to check for duplicates
          const numericPart = id.replace(/^supabase_/, '');
          
          if (seenIds.has(numericPart)) {
            // This is a duplicate - mark for deletion
            duplicatesToDelete.push(fav.item_id);
          } else {
            seenIds.add(numericPart);
          }
        });
        
        // Delete duplicates from database
        if (duplicatesToDelete.length > 0) {
          console.log('🧹 Cleaning up duplicate favorites:', duplicatesToDelete);
          for (const dupId of duplicatesToDelete) {
            await supabase
              .from('favorites')
              .delete()
              .eq('user_wallet', userWallet)
              .eq('item_type', 'piece')
              .eq('item_id', dupId);
          }
          
          // Reload favorites after cleanup
          const { data: cleanedFavorites, error: reloadError } = await supabase
            .from('favorites')
            .select('item_id')
            .eq('user_wallet', userWallet)
            .eq('item_type', 'piece')
            .order('created_at', { ascending: false });
          
          if (!reloadError && cleanedFavorites) {
            favorites = cleanedFavorites;
          }
        }
      }

      if (!favorites || favorites.length === 0) {
        setFavoritePiecesData([]);
        setFavoriteItems(new Set());
        return;
      }

      // Get the actual pieces
      const ids = favorites.map(f => f.item_id);
      
      // Try different approaches based on the pieces table structure
      let pieces, piecesError;
      
      // First, try treating IDs as text (if pieces.id is text)
      const { data: piecesAsText, error: textError } = await supabase
        .from('pieces')
        .select(`
          *,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            verification_type,
            profile_picture_url,
            total_pieces_created
          )
        `)
        .in('id', ids);
        
      if (textError && textError.code === '22P02') {
        // If that fails with bigint error, try converting to numbers
        const numericIds = ids.map(id => {
          // Try to extract number from strings like "supabase_15" -> 15
          const match = id.match(/\d+/);
          return match ? parseInt(match[0]) : null;
        }).filter(id => id !== null);
        
        const { data: piecesAsNumbers, error: numberError } = await supabase
          .from('pieces')
          .select(`
            *,
            creators (
              id,
              wallet,
              handle,
              is_verified,
              verification_type,
              profile_picture_url,
              total_pieces_created
            )
          `)
          .in('id', numericIds);
          
        pieces = piecesAsNumbers;
        piecesError = numberError;
      } else {
        pieces = piecesAsText;
        piecesError = textError;
      }

      if (piecesError) {
        console.error('Error fetching pieces:', piecesError);
        setFavoritePiecesData([]);
        setFavoriteItems(new Set());
        return;
      }

      // NOW: For each favorited piece, also fetch related pieces (for proper grouping)
      // This ensures that if you favorite 1 of 3 grouped pieces, all 3 show up
      console.log('🔍 Fetching related pieces for grouping...');
      const allPiecesMap = new Map();
      
      // Add favorited pieces
      (pieces || []).forEach(p => allPiecesMap.set(p.id, p));
      
      // For each piece, find related ones (same creator + name)
      for (const piece of (pieces || [])) {
        const { data: relatedPieces } = await supabase
          .from('pieces')
          .select(`
            *,
            creators (
              id,
              wallet,
              handle,
              is_verified,
              verification_type,
              profile_picture_url,
              total_pieces_created
            )
          `)
          .eq('creator_wallet', piece.creator_wallet)
          .eq('name', piece.name)
          .order('created_at', { ascending: false }); // Order newest first to match piece selector
        
        // Add related pieces to the map (avoid duplicates)
        (relatedPieces || []).forEach(p => {
          if (!allPiecesMap.has(p.id)) {
            allPiecesMap.set(p.id, p);
          }
        });
      }
      
      // Convert map back to array and sort by creation date (ensures consistent order)
      // Sort newest first to match the piece selector order in modal
      pieces = Array.from(allPiecesMap.values()).sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Descending order (newest first)
      });
      console.log(`✅ Expanded ${favorites.length} favorited pieces to ${pieces.length} total pieces (including related)`);

      // Normalize pieces using the same normalizer as Explore page
      const normalizedPieces = normalizePieces(pieces || []);
      
      console.log('🔍 Raw normalized pieces:', normalizedPieces.map(p => ({
        id: p.id,
        name: p.name,
        creator_wallet: p.creator_wallet || p.creators?.wallet,
        thumbnail_url: p.thumbnail_url || p.thumbnailUrl,
        model_url: p.model_url || p.preview_glb_url
      })));
      
      // Add any missing fields from the raw piece data
      const transformedPieces = normalizedPieces.map((piece) => ({
        ...piece,
        // Keep original database ID
        id: piece.id,
        // Ensure grouping fields are present (CRITICAL for grouping to work)
        creator_wallet: piece.creator_wallet || piece.creators?.wallet,
        creator_id: piece.creator_id || piece.creators?.id,
        thumbnail_url: piece.thumbnail_url || piece.thumbnailUrl,
        model_url: piece.model_url || piece.modelUrl || piece.preview_glb_url || piece.previewUrl,
        preview_glb_url: piece.preview_glb_url || piece.previewUrl,
        // Other fields
        name: piece.name,
        price: parseFloat(piece.price) || parseFloat(piece.listing_price) || 0,
        listing_price: piece.listing_price,
        listing_status: piece.listing_status,
        total_quantity: piece.total_quantity || 1,
        is_master: piece.is_master,
        master_id: piece.master_id,
        created_at: piece.created_at,
        is_public: piece.is_public,
        is_draft: piece.is_draft,
        mint_address: piece.mint_address,
        holder_wallet: piece.holder_wallet,
        creators: piece.creators
      }));

      console.log('🔗 Transformed pieces for grouping:', transformedPieces.map(p => ({
        id: p.id,
        name: p.name,
        creator_wallet: p.creator_wallet,
        thumbnail_url: p.thumbnail_url,
        model_url: p.model_url
      })));

      // Apply grouping logic (just like Explore page)
      console.log('🔗 Applying grouping logic to favorite pieces...');
      const groupedFavoritePieces = createGroupedPieceCards(transformedPieces);
      console.log('✅ Grouped favorite pieces:', groupedFavoritePieces.map(card => ({
        id: card.id,
        name: card.name,
        isGroup: card._isGroup,
        groupSize: card._groupSize,
        availableCount: card._availableCount
      })));
      console.log(`📊 Grouping result: ${transformedPieces.length} pieces → ${groupedFavoritePieces.length} cards`);

      setFavoritePiecesData(groupedFavoritePieces);
      
      // Only track the ACTUAL favorited pieces (not related ones) for heart icon display
      // Use the original favorites list from the database
      // Ensure IDs are strings for consistent comparison
      const actualFavoriteIds = favorites.map(f => String(f.item_id));
      setFavoriteItems(new Set(actualFavoriteIds));
      
      // Initialize local favorited state with actual favorites only
      console.log('✅ Setting localFavoritedPieces:', actualFavoriteIds);
      setLocalFavoritedPieces(new Set(actualFavoriteIds));
    } catch (error) {
      console.error('Error loading favorite pieces:', error);
      setFavoritePiecesData([]);
      setFavoriteItems(new Set());
      setLocalFavoritedPieces(new Set());
    }
  };

  // Load favorite capsules from Supabase using the correct query pattern
  const loadFavoriteCapsules = async () => {
    if (!isConnected || !publicKey) {
      console.log('⏭️ loadFavoriteCapsules: Wallet not connected, skipping');
      setFavoriteCapsulesData([]);
      return;
    }

    try {
      const userWallet = publicKey.toString().toLowerCase();
      console.log('🔄 loadFavoriteCapsules: Loading for wallet:', userWallet);
      
      // First, get the favorite capsule IDs
      const { data: favorites, error: favoritesError } = await supabase
        .from('favorites')
        .select('item_id, metadata')
        .eq('user_wallet', userWallet)
        .eq('item_type', 'capsule')
        .order('created_at', { ascending: false });

      if (favoritesError) {
        console.error('❌ Error fetching capsule favorites:', favoritesError);
        setFavoriteCapsulesData([]);
        return;
      }

      console.log('📦 loadFavoriteCapsules: Found favorites:', favorites);

      if (!favorites || favorites.length === 0) {
        console.log('⚠️ loadFavoriteCapsules: No capsule favorites found');
        setFavoriteCapsulesData([]);
        return;
      }

      // Transform the capsule data from metadata
      const transformedCapsules = favorites.map(fav => {
        const metadata = fav.metadata || {};
        return {
          id: fav.item_id,
          title: metadata.title || metadata.name,
          name: metadata.name || metadata.title,
          description: metadata.description,
          banner: metadata.banner || metadata.image,
          image: metadata.image || metadata.banner,
          creator: metadata.creator,
          pieceCount: metadata.pieceCount || metadata.pieces,
          avgPrice: metadata.avgPrice || metadata.price,
          price: metadata.price || metadata.avgPrice,
          dropDate: metadata.dropDate || metadata.createdAt,
          createdAt: metadata.createdAt || metadata.dropDate,
          capsuleNumber: metadata.capsuleNumber,
          addedAt: metadata.addedAt
        };
      });

      console.log('✅ loadFavoriteCapsules: Transformed capsules:', transformedCapsules);

      setFavoriteCapsulesData(transformedCapsules);
      
      // Initialize local favorited state with all current favorites
      const favoriteIds = transformedCapsules.map(capsule => capsule.id);
      setLocalFavoritedCapsules(new Set(favoriteIds));
      
      console.log('✅ loadFavoriteCapsules: Set favorite IDs:', favoriteIds);
    } catch (error) {
      console.error('❌ Error loading favorite capsules:', error);
      setFavoriteCapsulesData([]);
      setLocalFavoritedCapsules(new Set());
    }
  };

  // Load favorites from localStorage and match with piece data
  useEffect(() => {
    if (!isConnected) {
      setFavoritePiecesData([]);
      setFavoriteItems(new Set());
      setFavoriteCapsulesData([]);
      setFavoriteWalletsData([]);
      setFavoriteWallets(new Set());
      return;
    }

    // Load favorite pieces from Supabase
    loadFavoritePieces();
    
    // Load favorite capsules from Supabase
    loadFavoriteCapsules();

    // Only load localStorage favorites if not connected to wallet
    // When connected, we use Supabase data instead
    if (!isConnected) {
      // Load piece favorites from localStorage
      const savedPieceFavorites = localStorage.getItem('favoritePieces');
      if (savedPieceFavorites) {
        try {
          const pieceFavoritesArray = JSON.parse(savedPieceFavorites);
          setFavoriteItems(new Set(pieceFavoritesArray));

          // Load all pieces data
          const localStoragePieces = JSON.parse(localStorage.getItem('pieces') || '[]');
          
          // Transform closet data to match piece structure
          const closetPieces = CLOSET.map(item => ({
            id: item.id,
            name: item.name,
            description: item.description,
            category: item.category,
            color: item.color,
            price: item.price,
            brand: item.brand,
            rarity: item.rarity,
            quantity: item.quantity,
            totalMade: item.totalMade,
            views: Math.floor(Math.random() * 5000) + 100,
            likes: Math.floor(Math.random() * 500) + 10,
            downloads: Math.floor(Math.random() * 200) + 5,
            sold: Math.floor(Math.random() * item.totalMade * 0.8),
            createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
            modelUrl: item.modelUrl,
            creator: {
              username: 'Original Creator',
              walletAddress: '7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU',
              piecesCount: 45
            },
            holder: {
              username: 'Anonymous',
              walletAddress: '9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM'
            }
          }));

          // Transform localStorage pieces
          const transformedLocalPieces = localStoragePieces.map(piece => ({
            ...piece,
            views: piece.views || Math.floor(Math.random() * 5000) + 100,
            likes: piece.likes || Math.floor(Math.random() * 500) + 10,
            downloads: piece.downloads || Math.floor(Math.random() * 200) + 5,
            sold: piece.sold || 0,
            totalMade: piece.supply || 1,
            creator: piece.creator || {
              username: 'Creator',
              walletAddress: '7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU',
              piecesCount: 25
            },
            holder: piece.holder || {
              username: 'Anonymous',
              walletAddress: '9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM'
            }
          }));

          // Transform trending pieces to match piece structure
          const trendingPieces = homeData.trendingPieces.map(item => ({
            id: item.id,
            name: item.name,
            description: item.description,
            category: item.category,
            color: item.color,
            price: parseFloat(item.price),
            brand: item.brand || 'Unknown',
            rarity: item.rarity || 'Common',
            quantity: item.quantity || 1,
            totalMade: item.totalMade || 100,
            views: item.views || Math.floor(Math.random() * 5000) + 100,
            likes: item.likes || Math.floor(Math.random() * 500) + 10,
            downloads: item.downloads || Math.floor(Math.random() * 200) + 5,
            sold: item.sold || Math.floor(Math.random() * (item.totalMade || 100) * 0.8),
            createdAt: item.createdAt,
            modelUrl: item.modelUrl,
            creator: {
              username: item.creator?.replace('@', '') || 'Unknown',
              walletAddress: '7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU',
              piecesCount: 45,
              verified: item.creatorVerified || false
            },
            holder: {
              username: 'Anonymous',
              walletAddress: '9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM'
            }
          }));

          const allPieces = [...closetPieces, ...transformedLocalPieces, ...trendingPieces];
          
          // Filter to only show favorited pieces
          const favoritedPieces = allPieces.filter(piece => pieceFavoritesArray.includes(piece.id));
          setFavoritePiecesData(favoritedPieces);
        } catch (error) {
          console.error('Error loading piece favorites:', error);
          setFavoritePiecesData([]);
          setFavoriteItems(new Set());
        }
      } else {
        setFavoritePiecesData([]);
        setFavoriteItems(new Set());
      }
    }

    // Load capsule favorites
    const savedCapsuleFavorites = localStorage.getItem('favoriteCapsules');
    if (savedCapsuleFavorites) {
      try {
        const capsuleFavoritesArray = JSON.parse(savedCapsuleFavorites);
        
        // Use the actual capsule data from localStorage
        // The capsuleFavoritesArray already contains the full capsule data with banner, price, etc.
        setFavoriteCapsulesData(capsuleFavoritesArray);
      } catch (error) {
        console.error('Error loading capsule favorites:', error);
        setFavoriteCapsulesData([]);
      }
    } else {
      setFavoriteCapsulesData([]);
    }

    // Load wallet favorites
    const savedWalletFavorites = localStorage.getItem('favoriteWallets');
    if (savedWalletFavorites) {
      try {
        const walletFavoritesArray = JSON.parse(savedWalletFavorites);
        setFavoriteWallets(new Set(walletFavoritesArray));

        // Load all wallets data - check localStorage for saved creator data first
        const savedCreators = JSON.parse(localStorage.getItem('savedCreators') || '[]')
          .map(creator => ({
            ...creator,
            // Ensure verificationType exists (migration for old data)
            verificationType: creator.verificationType || (creator.verified ? 'sales_milestone' : 'none')
          }));
        const mockWallets = [
          {
            id: 'w1',
            username: 'CryptoCollector',
            walletAddress: '7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU',
            piecesCount: 45,
            followers: 1200,
            following: 300,
            verified: true,
            verificationType: 'sales_milestone',
            avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
            bio: 'Digital fashion enthusiast and collector',
            joinedDate: '2023-01-15',
            totalVolume: 125.5,
            avgPrice: 2.8
          },
          {
            id: 'w2',
            username: 'FashionForward',
            walletAddress: '9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM',
            piecesCount: 23,
            followers: 850,
            following: 150,
            verified: false,
            verificationType: 'none',
            avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face',
            bio: 'Creating unique digital fashion pieces',
            joinedDate: '2023-03-22',
            totalVolume: 89.2,
            avgPrice: 3.2
          },
          {
            id: 'w3',
            username: 'StyleMaster',
            walletAddress: '5Q544fKrFoe6tsEbD5S2Zm4K5Rd7pRjo3zFJu6hFZxfr',
            piecesCount: 67,
            followers: 2100,
            following: 450,
            verified: true,
            verificationType: 'paid',
            avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
            bio: 'Premium digital fashion curator',
            joinedDate: '2022-11-08',
            totalVolume: 245.8,
            avgPrice: 4.1
          }
        ];
        
        // Combine saved creators with mock wallets
        const allWallets = [...savedCreators, ...mockWallets];
        
        // Filter to only show favorited wallets
        const favoritedWallets = allWallets.filter(wallet => walletFavoritesArray.includes(wallet.id));
        setFavoriteWalletsData(favoritedWallets);
      } catch (error) {
        console.error('Error loading wallet favorites:', error);
        setFavoriteWalletsData([]);
        setFavoriteWallets(new Set());
      }
    } else {
      setFavoriteWalletsData([]);
      setFavoriteWallets(new Set());
    }
  }, [isConnected]);

  // Reload favorite pieces when connected or when returning to the page
  useEffect(() => {
    if (isConnected) {
      console.log('🔄 Favorites page: Loading favorites on mount/reconnect');
      loadFavoritePieces();
      loadFavoriteCapsules();
    }
  }, [isConnected, publicKey]);

  // Listen for favorite changes and reload immediately
  useEffect(() => {
    const handleFavoritesChange = (e) => {
      console.log('🔄 Favorites page: Received favorite change event, reloading favorites');
      if (isConnected) {
        // Reload to show current state from database
        loadFavoritePieces();
        loadFavoriteCapsules();
      }
    };

    window.addEventListener('pieceFavoritesChanged', handleFavoritesChange);
    window.addEventListener('capsuleFavoritesChanged', handleFavoritesChange);
    
    return () => {
      window.removeEventListener('pieceFavoritesChanged', handleFavoritesChange);
      window.removeEventListener('capsuleFavoritesChanged', handleFavoritesChange);
    };
  }, [isConnected, publicKey]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch active listing when piece detail modal opens
  useEffect(() => {
    const fetchActiveListing = async () => {
      if (!pieceDetailModal.isOpen || !pieceDetailModal.item?.mint_address) {
        setActiveListing(null);
        return;
      }

      try {
        setListingLoading(true);
        const listing = await getActiveListing(pieceDetailModal.item.mint_address);
        setActiveListing(listing);
      } catch (error) {
        console.error('Error fetching active listing:', error);
        setActiveListing(null);
      } finally {
        setListingLoading(false);
      }
    };

    fetchActiveListing();
  }, [pieceDetailModal.isOpen, pieceDetailModal.item?.mint_address]);

  // Real-time subscription for listing and piece changes
  useEffect(() => {
    if (!pieceDetailModal.isOpen || !pieceDetailModal.item?.mint_address) {
      return;
    }
    
    console.log('👁️ Setting up real-time subscriptions for:', pieceDetailModal.item.mint_address);
    
    // Subscribe to listing changes
    const listingSubscription = subscribeToListing(pieceDetailModal.item.mint_address, (payload) => {
      console.log('📡 Listing update received:', payload);
      
      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
        if (payload.new.status === 'active') {
          setActiveListing(payload.new);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'listed',
              listing_price: payload.new.price_sol
            }
          }));
        } else if (payload.new.status === 'filled') {
          setActiveListing(null);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'sold',
              holder_wallet: payload.new.buyer_wallet
            }
          }));
        } else if (payload.new.status === 'canceled') {
          // Listing canceled - remove buy button
          setActiveListing(null);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'unlisted',
              listing_price: null
            }
          }));
        }
      } else if (payload.eventType === 'DELETE') {
        setActiveListing(null);
      }
    });
    
    // Subscribe to piece changes
    const pieceSubscription = subscribeToPiece(pieceDetailModal.item.mint_address, (payload) => {
      console.log('📡 Piece update received:', payload);
      
      if (payload.eventType === 'UPDATE') {
        setPieceDetailModal(prev => ({
          ...prev,
          item: {
            ...prev.item,
            ...payload.new
          }
        }));
      }
    });
    
    // Cleanup subscriptions
    return () => {
      console.log('🔌 Cleaning up subscriptions...');
      listingSubscription.unsubscribe();
      pieceSubscription.unsubscribe();
    };
  }, [pieceDetailModal.isOpen, pieceDetailModal.item?.mint_address]);

  // Sort options
  const sortOptions = [
    { id: 'recent', name: 'Most Recent' },
    { id: 'name', name: 'Name A-Z' },
    { id: 'price-low', name: 'Price: Low to High' },
    { id: 'price-high', name: 'Price: High to Low' },
    { id: 'rarity', name: 'Rarity' }
  ];

  // Handle dropdown change
  const handleSortChange = (optionId) => {
    setSortBy(optionId);
    setIsSortDropdownOpen(false);
  };

  // Mock favorites data for pieces
  const favoritePieces = [
    {
      id: 1,
      type: 'piece',
      name: 'Beanie',
      description: 'Warm beanie made from premium wool',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300&h=300&fit=crop',
      price: 0.25,
      floorPrice: 0.25,
      category: 'hats',
      color: 'navy',
      quantity: 7,
      totalMade: 50,
      favoritedDate: '2024-01-10',
      verified: true,
      creator: {
        username: 'FashionDesigner',
        walletAddress: '7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU',
        piecesCount: 45
      },
      holder: {
        username: 'Anonymous',
        walletAddress: '9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM'
      }
    },
    {
      id: 2,
      type: 'piece',
      name: 'Boxy Hoodie',
      description: 'Limited edition boxy cut hoodie with unique stitching',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=300&h=300&fit=crop',
      price: 0.8,
      floorPrice: 0.8,
      category: 'tops',
      color: 'gray',
      quantity: 5,
      totalMade: 20,
      favoritedDate: '2024-01-12',
      verified: false,
      creator: {
        username: 'MetaverseStylist',
        walletAddress: '9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM',
        piecesCount: 32
      },
      holder: {
        username: 'Collector123',
        walletAddress: 'CwNLhqK3fYwE1jqVvJ8qYzN2kL5mP9rS3tU6wX4yB7eA'
      }
    },
    {
      id: 3,
      type: 'piece',
      name: 'Bucket Hat',
      description: 'Trendy bucket hat perfect for summer',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=300&fit=crop',
      price: 0.15,
      floorPrice: 0.15,
      category: 'hats',
      color: 'black',
      quantity: 1,
      totalMade: 150,
      favoritedDate: '2024-01-11',
      verified: false
    },
    {
      id: 4,
      type: 'piece',
      name: 'Cargo Shorts',
      description: 'Comfortable cargo shorts for outdoor activities',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=300&h=300&fit=crop',
      price: 0.4,
      floorPrice: 0.4,
      category: 'bottoms',
      color: 'olive',
      quantity: 1,
      totalMade: 250,
      favoritedDate: '2024-01-09',
      verified: true
    },
    {
      id: 5,
      type: 'piece',
      name: 'Carpenter Pants',
      description: 'Durable work pants with multiple pockets',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300&h=300&fit=crop',
      price: 0.55,
      floorPrice: 0.55,
      category: 'bottoms',
      color: 'khaki',
      quantity: 1,
      totalMade: 150,
      favoritedDate: '2024-01-08',
      verified: true
    },
    {
      id: 6,
      type: 'piece',
      name: 'Vintage T-Shirt',
      description: 'Classic vintage style t-shirt with retro graphics',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=300&fit=crop',
      price: 0.3,
      floorPrice: 0.3,
      category: 'tops',
      color: 'white',
      quantity: 12,
      totalMade: 100,
      favoritedDate: '2024-01-07',
      verified: false
    }
  ];



  const typeFilters = [
    "All", "Tops", "Bottoms", "Hats", "Shoes", "Accessories"
  ];




  // Sort data based on selected sort option
  const sortData = (data) => {
    if (!data || data.length === 0) return data;
    
    const sortedData = [...data];
    
    switch (sortBy) {
      case 'recent':
        // Sort by most recent (createdAt or id)
        return sortedData.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0);
          const dateB = new Date(b.createdAt || b.created_at || 0);
          return dateB - dateA;
        });
      
      case 'name':
        // Sort by name A-Z
        return sortedData.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      
      case 'price-low':
        // Sort by price: Low to High
        return sortedData.sort((a, b) => {
          const priceA = parseFloat(a.price || a.lowestPrice || a.floorPrice || 0);
          const priceB = parseFloat(b.price || b.lowestPrice || b.floorPrice || 0);
          return priceA - priceB;
        });
      
      case 'price-high':
        // Sort by price: High to Low
        return sortedData.sort((a, b) => {
          const priceA = parseFloat(a.price || a.lowestPrice || a.floorPrice || 0);
          const priceB = parseFloat(b.price || b.lowestPrice || b.floorPrice || 0);
          return priceB - priceA;
        });
      
      case 'rarity':
        // Sort by rarity (if available)
        const rarityOrder = { 'Legendary': 5, 'Epic': 4, 'Rare': 3, 'Uncommon': 2, 'Common': 1 };
        return sortedData.sort((a, b) => {
          const rarityA = rarityOrder[a.rarity] || 0;
          const rarityB = rarityOrder[b.rarity] || 0;
          return rarityB - rarityA;
        });
      
      default:
        return sortedData;
    }
  };

  // Get current data based on active tab
  const getCurrentData = () => {
    let data;
    
    if (activeTab === 'pieces') {
      // If wallet is connected, use real favorite pieces data, otherwise use mock data
      data = isConnected ? favoritePiecesData : favoritePieces;
    } else if (activeTab === 'capsules') {
      // If wallet is connected, use real favorite capsules data, otherwise use mock data
      data = isConnected ? favoriteCapsulesData : favoriteCapsulesData;
    } else if (activeTab === 'wallets') {
      // If wallet is connected, use real favorite wallets data, otherwise use mock data
      data = isConnected ? favoriteWalletsData : favoriteWalletsData;
    } else {
      data = isConnected ? favoritePiecesData : favoritePieces;
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      data = data.filter(item => {
        const name = (item.name || '').toLowerCase();
        const description = (item.description || '').toLowerCase();
        const category = (item.category || '').toLowerCase();
        const brand = (item.brand || '').toLowerCase();
        
        return name.includes(searchLower) || 
               description.includes(searchLower) || 
               category.includes(searchLower) ||
               brand.includes(searchLower);
      });
    }
    
    // Apply sorting
    return sortData(data);
  };


  // Modal handlers
  const openPieceDetailModal = async (item) => {
    console.log('🔍 openPieceDetailModal: Opening modal for item:', item);
    console.log('🔍 Item price data:', {
      id: item?.id,
      name: item?.name,
      price: item?.price,
      listing_price: item?.listing_price,
      lowestPrice: item?.lowestPrice
    });
    
    // Check if this is a grouped card - if so, show grouped pieces modal instead
    if (isGroupedCard(item)) {
      console.log('🔗 Grouped card detected - showing grouped pieces modal');
      console.log(`  - Group size: ${item._groupSize}`);
      console.log(`  - Available: ${item._availableCount}`);
      setGroupedPiecesModal({ isOpen: true, groupedCard: item });
      return;
    }
    
    // Refetch fresh piece data from database to ensure we have complete data including price
    try {
      // Extract numeric ID from string like "supabase_10882" or just "10882"
      const numericId = typeof item.id === 'string' && item.id.includes('supabase_')
        ? parseInt(item.id.replace('supabase_', ''))
        : parseInt(item.id);
      
      console.log('🔢 Extracted numeric ID:', numericId, 'from:', item.id);
      
      const { data: freshPiece, error } = await supabase
        .from('pieces')
        .select(`
          *,
          social_links,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            verification_type,
            profile_picture_url,
            banner_url,
            bio,
            social_links,
            total_pieces_created
          )
        `)
        .eq('id', numericId)
        .single();
      
      if (!error && freshPiece) {
        console.log('✅ Refetched fresh piece data:', {
          id: freshPiece.id,
          name: freshPiece.name,
          price: freshPiece.price,
          listing_price: freshPiece.listing_price,
          total_quantity: freshPiece.total_quantity,
          master_id: freshPiece.master_id
        });
        
        // Check if this piece has related editions (for proper holder display)
        // Use the SAME grouping logic as Explore page (by name, creator, URLs)
        let relatedPieceIds = null;
        
        console.log('🔍 Looking for grouped pieces using matching attributes...');
        console.log(`   Matching: creator=${freshPiece.creator_wallet}, name="${freshPiece.name}"`);
        
        // Fetch all pieces with matching grouping attributes (same as pieceGrouping.js logic)
        // Match by: creator + name + thumbnail + model_url
        const modelUrl = freshPiece.model_url || freshPiece.preview_glb_url || '';
        const { data: relatedPieces, error: relatedError } = await supabase
          .from('pieces')
          .select('id, holder_wallet, edition_number, name, created_at, model_url, preview_glb_url, thumbnail_url')
          .eq('creator_wallet', freshPiece.creator_wallet)
          .eq('name', freshPiece.name)
          .order('created_at', { ascending: false }); // Newest first to match piece selector
        
        // Filter by thumbnail and model URLs (client-side since Supabase doesn't handle COALESCE well in filters)
        const filteredPieces = relatedPieces?.filter(p => {
          const pThumbnail = p.thumbnail_url || '';
          const pModel = p.model_url || p.preview_glb_url || '';
          const freshThumbnail = freshPiece.thumbnail_url || '';
          return pThumbnail === freshThumbnail && pModel === modelUrl;
        });
        
        if (relatedError) {
          console.error('❌ Error fetching grouped pieces:', relatedError);
        } else if (filteredPieces && filteredPieces.length > 1) {
          // Found multiple pieces with same attributes - they're grouped!
          relatedPieceIds = filteredPieces.map(p => p.id);
          console.log(`✅ Found ${filteredPieces.length} grouped pieces:`, filteredPieces.map(p => ({
            id: p.id,
            holder: p.holder_wallet?.slice(0, 8),
            edition: p.edition_number
          })));
        } else if (filteredPieces && filteredPieces.length === 1) {
          console.log('✅ Single piece, no grouping needed');
        } else {
          console.warn('⚠️ No related pieces found');
        }
        
        setCurrentPieceGroupIds(relatedPieceIds);
        
        // Merge fresh data with existing item data (freshPiece.id is numeric from DB)
        const mergedItem = { ...item, ...freshPiece };
        // Normalize the piece data to ensure consistent field names
        const normalizedItem = normalizePieces([mergedItem])[0];
        console.log('📋 Opening modal with normalized item. ID:', normalizedItem.id, 'Type:', typeof normalizedItem.id);
        setPieceDetailModal({ isOpen: true, item: normalizedItem });
        
        // Track view (only if not already tracked in this session)
        if (normalizedItem?.id && lastTrackedPiece.current !== String(normalizedItem.id)) {
          const walletAddress = publicKey?.toString() || null;
          const pieceIdString = String(normalizedItem.id);
          const result = await trackPieceView(pieceIdString, walletAddress);
          if (result.success) {
            console.log('✅ [FAVORITES] View tracked:', result.stats);
            lastTrackedPiece.current = pieceIdString;
          } else {
            console.log('⏭️ [FAVORITES] View not tracked:', result.error);
            lastTrackedPiece.current = pieceIdString;
          }
        }
      } else {
        console.warn('⚠️ Could not refetch piece data, using existing:', error);
        setCurrentPieceGroupIds(null);
        // Normalize the item to ensure consistent field names
        const normalizedItem = normalizePieces([item])[0];
        setPieceDetailModal({ isOpen: true, item: normalizedItem });
        
        // Track view (only if not already tracked in this session)
        if (normalizedItem?.id && lastTrackedPiece.current !== String(normalizedItem.id)) {
          const walletAddress = publicKey?.toString() || null;
          const pieceIdString = String(normalizedItem.id);
          const result = await trackPieceView(pieceIdString, walletAddress);
          if (result.success) {
            console.log('✅ [FAVORITES] View tracked:', result.stats);
            lastTrackedPiece.current = pieceIdString;
          } else {
            console.log('⏭️ [FAVORITES] View not tracked:', result.error);
            lastTrackedPiece.current = pieceIdString;
          }
        }
      }
    } catch (error) {
      console.error('❌ Error refetching piece:', error);
      setCurrentPieceGroupIds(null);
      // Normalize the item to ensure consistent field names
      const normalizedItem = normalizePieces([item])[0];
      setPieceDetailModal({ isOpen: true, item: normalizedItem });
      
      // Track view (only if not already tracked in this session)
      if (normalizedItem?.id && lastTrackedPiece.current !== String(normalizedItem.id)) {
        const walletAddress = publicKey?.toString() || null;
        const pieceIdString = String(normalizedItem.id);
        const result = await trackPieceView(pieceIdString, walletAddress);
        if (result.success) {
          console.log('✅ [FAVORITES] View tracked:', result.stats);
          lastTrackedPiece.current = pieceIdString;
        } else {
          console.log('⏭️ [FAVORITES] View not tracked:', result.error);
          lastTrackedPiece.current = pieceIdString;
        }
      }
    }
    
    // Check if piece has 3D model and set initial view mode
    const normalizedPiece = item ? normalizePieces([item])[0] : null;
    const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
    
    if (has3DModel) {
      setModalViewMode('3d');
      setThreeDStatus('loading');
      setThreeDError(null);
    } else {
      setModalViewMode('thumbnail');
      setThreeDStatus('error');
      setThreeDError('No 3D model available');
    }

    // Check if piece belongs to a capsule
    if (item?.id) {
      try {
        // Extract original ID from prefixed ID (supabase_123 -> 123, local_456_0 -> 456)
        let originalId = item.id;
        const itemIdString = String(item.id);
        if (itemIdString.startsWith('supabase_')) {
          originalId = itemIdString.replace('supabase_', '');
          // Convert to number if it's a numeric ID
          if (!isNaN(originalId)) {
            originalId = parseInt(originalId, 10);
          }
        } else if (itemIdString.startsWith('local_')) {
          // For local pieces, they typically don't belong to capsules
          console.log('🔍 Local piece detected, skipping capsule check');
          setPieceCapsuleInfo(null);
          return;
        }
        
        console.log('🔍 Checking capsule for piece:', { originalId, transformedId: item.id, originalIdType: typeof originalId });
        const capsuleInfo = await getCapsuleForPiece(originalId);
        console.log('🔍 Capsule info result:', capsuleInfo);
        setPieceCapsuleInfo(capsuleInfo);
      } catch (error) {
        console.error('Error fetching capsule info for piece:', error);
        setPieceCapsuleInfo(null);
      }
    } else {
      setPieceCapsuleInfo(null);
    }
    
    // Load active listing if piece is listed
    if (item?.listing_status === 'listed' || item?.is_delegate_active) {
      console.log('📋 Loading active listing for piece:', {
        mint_address: item?.mint_address,
        listing_status: item?.listing_status,
        is_delegate_active: item?.is_delegate_active,
        piece_id: item?.id
      });
      
      try {
        // Try to fetch listing by mint address first
        let listing = null;
        if (item?.mint_address) {
          listing = await getActiveListing(item.mint_address);
          console.log('✅ Listing loaded by mint:', listing);
        }
        
        // FALLBACK: If no listing found by mint, query database directly by piece_id
        if (!listing && item?.id) {
          // Extract numeric ID from prefixed ID (supabase_10601 -> 10601)
          let numericPieceId = item.id;
          const itemIdString = String(item.id);
          if (itemIdString.startsWith('supabase_')) {
            numericPieceId = itemIdString.replace('supabase_', '');
            // Convert to number if it's a numeric ID
            if (!isNaN(numericPieceId)) {
              numericPieceId = parseInt(numericPieceId, 10);
            }
          } else if (itemIdString.startsWith('local_')) {
            console.log('📋 Local piece detected, skipping database fallback');
            numericPieceId = null;
          }
          
          if (numericPieceId) {
            console.log('📋 Trying database fallback for numeric piece_id:', numericPieceId);
            const { data: dbListing, error } = await supabase
              .from('listings')
              .select('*')
              .eq('piece_id', numericPieceId)
              .eq('status', 'active')
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            
            if (!error && dbListing) {
              console.log('✅ Listing loaded from database:', dbListing);
              listing = dbListing;
            } else if (error) {
              console.error('❌ Database query error:', error);
            }
          }
        }
        
        setActiveListing(listing);
        if (!listing) {
          console.warn('⚠️ No active listing found for this piece');
        }
      } catch (error) {
        console.error('❌ Failed to load listing:', error);
        setActiveListing(null);
      }
    } else {
      console.log('📋 No active listing (status not listed)');
      setActiveListing(null);
    }
  };

  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, item: null });
    setCurrentPieceGroupIds(null); // Reset grouped piece IDs
    setModalViewMode('3d');
    setThreeDStatus('loading');
    setThreeDError(null);
    setPieceCapsuleInfo(null);
    setActivityPanelOpen(false);
    // Clear resale state
    setActiveListing(null);
    setShowListModal(false);
  };

  // Handle buy now button
  const handleBuyNow = withWalletCheck((item) => {
    setBuyModal({ isOpen: true, item });
  }, 'make a purchase');

  // Handle request trade button
  const handleRequestTrade = withWalletCheck((item) => {
    setRequestTradeModal({ isOpen: true, item });
  }, 'request a trade');

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    }
  };

  // Handle toggle favorite - syncs between card and modal
  const handleToggleFavorite = async (item) => {
    if (activeTab === 'pieces') {
      if (isConnected) {
        try {
          const userWallet = publicKey.toString().toLowerCase();
          
          // For grouped pieces, check if ANY piece in the group is favorited
          // Ensure consistent ID comparison (convert to string)
          const piecesToCheck = item._isGroup ? item._groupPieces : [item];
          const favoritedPiecesInGroup = piecesToCheck.filter(p => localFavoritedPieces.has(String(p.id)));
          const isAnyFavorited = favoritedPiecesInGroup.length > 0;

          if (isAnyFavorited) {
            // Remove ALL favorited pieces in the group from favorites
            for (const piece of favoritedPiecesInGroup) {
              const { error } = await supabase
                .from('favorites')
                .delete()
                .eq('user_wallet', userWallet)
                .eq('item_type', 'piece')
                .eq('item_id', String(piece.id));

              if (error) {
                console.error('Error removing favorite:', error);
                // Continue with other pieces even if one fails
              }
            }

            // Update local state (but keep item visible on Favorites page)
            setLocalFavoritedPieces(prev => {
              const newSet = new Set(prev);
              favoritedPiecesInGroup.forEach(piece => newSet.delete(String(piece.id)));
              return newSet;
            });
            
            // Notify all components (but DON'T remove from displayed list)
            window.dispatchEvent(new Event('pieceFavoritesChanged'));
          } else {
            // Re-add to favorites (use the representative piece ID)
            const pieceIdToFavorite = String(item.id);
            const { error } = await supabase
              .from('favorites')
              .insert({
                user_wallet: userWallet,
                item_type: 'piece',
                item_id: pieceIdToFavorite
              });

            if (error) {
              console.error('Error adding favorite:', error);
              return;
            }

            // Update local state
            setLocalFavoritedPieces(prev => new Set([...prev, pieceIdToFavorite]));
            
            // Notify all components
            window.dispatchEvent(new Event('pieceFavoritesChanged'));
          }
        } catch (error) {
          console.error('Error toggling favorite:', error);
        }
      } else {
        // Fallback to localStorage when not connected
        const newFavorites = new Set(favoriteItems);
        if (newFavorites.has(item.id)) {
          newFavorites.delete(item.id);
        } else {
          newFavorites.add(item.id);
        }
        setFavoriteItems(newFavorites);
        
        // Update localStorage
        localStorage.setItem('favoritePieces', JSON.stringify([...newFavorites]));
      }
    } else if (activeTab === 'capsules') {
      if (isConnected) {
        try {
          const userWallet = publicKey.toString().toLowerCase();
          const isCurrentlyFavorited = localFavoritedCapsules.has(item.id);

          if (isCurrentlyFavorited) {
            // Remove from favorites
            const { error } = await supabase
              .from('favorites')
              .delete()
              .eq('user_wallet', userWallet)
              .eq('item_type', 'capsule')
              .eq('item_id', item.id);

            if (error) {
              console.error('Error removing capsule favorite:', error);
              return;
            }

            // Update local state
            setLocalFavoritedCapsules(prev => {
              const newSet = new Set(prev);
              newSet.delete(item.id);
              return newSet;
            });
            
            // Notify all components
            window.dispatchEvent(new Event('capsuleFavoritesChanged'));
          } else {
            // Re-add to favorites
            const { error } = await supabase
              .from('favorites')
              .insert({
                user_wallet: userWallet,
                item_type: 'capsule',
                item_id: item.id,
                metadata: {
                  title: item.title || item.name,
                  name: item.name || item.title,
                  description: item.description,
                  banner: item.banner || item.image,
                  image: item.image || item.banner,
                  creator: item.creator,
                  pieceCount: item.pieceCount,
                  avgPrice: item.avgPrice || item.price,
                  price: item.price || item.avgPrice,
                  dropDate: item.dropDate || item.createdAt,
                  createdAt: item.createdAt || item.dropDate,
                  capsuleNumber: item.capsuleNumber,
                  addedAt: new Date().toISOString()
                }
              });

            if (error) {
              console.error('Error adding capsule favorite:', error);
              return;
            }

            // Update local state
            setLocalFavoritedCapsules(prev => new Set([...prev, item.id]));
            
            // Notify all components
            window.dispatchEvent(new Event('capsuleFavoritesChanged'));
          }
        } catch (error) {
          console.error('Error toggling capsule favorite:', error);
        }
      } else {
        // Fallback to localStorage when not connected
        toggleCapsuleFavorite(item);
        
        // Update the displayed favorites data
        if (isCapsuleFavorited(item.id)) {
          // Item is currently favorited, so it will be removed
          setFavoriteCapsulesData(prev => prev.filter(capsule => capsule.id !== item.id));
        }
      }
    } else if (activeTab === 'wallets') {
      const newFavorites = new Set(favoriteWallets);
      if (newFavorites.has(item.id)) {
        newFavorites.delete(item.id);
      } else {
        newFavorites.add(item.id);
      }
      setFavoriteWallets(newFavorites);
    }
  };

  // Process purchase with wallet
  const processPurchase = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    const { item } = buyModal;
    const price = parseFloat(item.price);
    
    if (balance < price) {
      showNotification(`Insufficient balance. You need ${price} SOL but only have ${balance.toFixed(4)} SOL`, 'error');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Simulate transaction processing
      console.log(`Processing purchase of ${item.name} for ${price} SOL`);
      console.log(`From wallet: ${publicKey.toString()}`);
      
      // Here you would integrate with your actual payment system
      // For now, we'll simulate a successful transaction
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Show success notification
      showNotification(`Successfully purchased ${item.name} for ${price} SOL! 🎉`, 'success');
      
      // Close modal
      setBuyModal({ isOpen: false, item: null });
      
      // In a real app, you would:
      // 1. Create a transaction
      // 2. Send SOL to the seller
      // 3. Transfer the NFT to the buyer
      // 4. Update the database
      
    } catch (error) {
      console.error('Purchase failed:', error);
      showNotification('Purchase failed. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Close buy modal
  const closeBuyModal = () => {
    setBuyModal({ isOpen: false, item: null });
  };

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  // Handle make offer button
  const handleMakeOffer = withWalletCheck((item) => {
    setOfferModal({ isOpen: true, item, offerAmount: '', expirationDays: 7 });
  }, 'make an offer');

  // Process offer with wallet
  const processOffer = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    const { item, offerAmount, expirationDays } = offerModal;
    const offerPrice = parseFloat(offerAmount);
    
    if (!offerPrice || offerPrice <= 0 || isNaN(offerPrice)) {
      showNotification('Please enter a valid offer amount', 'error');
      return;
    }

    if (balance < offerPrice) {
      showNotification(`Insufficient balance. You need ${offerPrice} SOL but only have ${balance.toFixed(4)} SOL`, 'error');
      return;
    }

    // Validate required fields
    if (!item.id || !item.mint_address) {
      showNotification('This piece is not ready for offers yet. Please try again later.', 'error');
      return;
    }

    // Extract the real piece ID (remove 'supabase_' prefix if present)
    let realPieceId = item.id;
    if (typeof item.id === 'string' && item.id.startsWith('supabase_')) {
      realPieceId = parseInt(item.id.replace('supabase_', ''));
    }

    setIsProcessing(true);
    
    try {
      console.log(`📝 Creating offer for ${item.name} of ${offerPrice} SOL`);
      
      // Create offer using the real database function
      const result = await createOffer(wallet, {
        pieceId: realPieceId,
        mintAddress: item.mint_address,
        pieceName: item.name,
        offerPrice: offerPrice,
        message: '',
        expirationDays: expirationDays || 7,
      });
      
      if (!result.success) {
        // Check if it's a duplicate offer error
        if (result.error && result.error.includes('already have an active offer')) {
          showNotification(
            'You already have an active offer on this piece. Visit the Offers page to view or update it.',
            'error'
          );
          setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
          return;
        }
        throw new Error(result.error || 'Failed to create offer');
      }
      
      console.log('✅ Offer created successfully!');
      
      // Show success notification
      showNotification(`Offer of ${offerPrice} SOL submitted successfully! 🎉`, 'success');
      
      // Close modal
      setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
      
    } catch (error) {
      console.error('❌ Offer creation failed:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Unable to create offer. Please try again.';
      
      // Check if error message contains the duplicate offer info
      if (error.message && error.message.includes('already have an active offer')) {
        errorMessage = 'You already have an active offer on this piece. Visit the Offers page to view or update it.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotification(errorMessage, 'error');
      
      // Close modal if it's a duplicate offer error
      if (errorMessage.includes('already have an active offer')) {
        setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Close offer modal
  const closeOfferModal = () => {
    setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
  };

  // Handle purchase listing from resale marketplace - open confirmation modal
  const handlePurchaseListing = withWalletCheck(() => {
    const { item } = pieceDetailModal;
    
    if (!activeListing) {
      showNotification('No active listing found', 'error');
      return;
    }
    
    // Open purchase confirmation modal
    setShowPurchaseConfirmModal(true);
  }, 'purchase this listing');

  // Confirm and process purchase
  const confirmPurchaseListing = async () => {
    const { item } = pieceDetailModal;
    
    if (!activeListing) {
      showNotification('No active listing found', 'error');
      return;
    }
    
    try {
      setListingLoading(true);
      console.log('🛒 Purchasing from listing:', item.name);
      
      const result = await purchaseListing(wallet, item, activeListing, activeListing.price_sol);
      
      console.log('✅ Purchase successful:', result);
      showNotification(`Successfully purchased ${item.name} for ${activeListing.price_sol} SOL! 🎉`, 'success');
      
      setShowPurchaseConfirmModal(false);
      
      // Reload pieces to show updated ownership
      window.location.reload();
      
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      showNotification(error.message || 'Purchase failed. Please try again.', 'error');
      setShowPurchaseConfirmModal(false);
    } finally {
      setListingLoading(false);
    }
  };

  // Handle cancel listing
  const handleCancelListing = async () => {
    const { item } = pieceDetailModal;
    
    if (!confirm(`Cancel listing for ${item.name}?`)) {
      return;
    }
    
    try {
      setListingLoading(true);
      console.log('❌ Canceling listing...');
      
      const result = await cancelListing(wallet, activeListing, item);
      
      console.log('✅ Listing canceled:', result);
      showNotification('Listing canceled successfully! 🎉', 'success');
      
      // Reload to update listing status
      window.location.reload();
      
    } catch (error) {
      console.error('❌ Cancel listing failed:', error);
      showNotification(error.message || 'Failed to cancel listing. Please try again.', 'error');
    } finally {
      setListingLoading(false);
    }
  };

  // Handle view profile navigation
  const handleViewProfile = (wallet) => {
    // Use username for better user experience, fallback to walletAddress if username is not available
    const creatorId = wallet.username || wallet.walletAddress;
    navigate(`/creator/${creatorId}`);
  };

  // Handle view capsule navigation
  const handleViewCapsule = (capsule) => {
    navigate(`/capsule/${capsule.id}`);
  };

  return (
    <WalletRequired 
      message="You need to connect your wallet to view and manage your favorites."
      action="manage favorites"
    >
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="max-w-full mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Favorites</h1>
          <p className="text-gray-400">Your saved items and collections</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-8 mb-4">
          <button
            onClick={() => setActiveTab('pieces')}
            className={`relative pb-2 text-sm font-medium transition-colors ${
              activeTab === 'pieces'
                ? 'text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Pieces
            {activeTab === 'pieces' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab('capsules')}
            className={`relative pb-2 text-sm font-medium transition-colors ${
              activeTab === 'capsules'
                ? 'text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Capsules
            {activeTab === 'capsules' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab('wallets')}
            className={`relative pb-2 text-sm font-medium transition-colors ${
              activeTab === 'wallets'
                ? 'text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Wallets
            {activeTab === 'wallets' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"></div>
            )}
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 py-6 border-b border-[#2a2a2a] bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search favorites..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-[#2a2a2a] rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                  style={{ backgroundColor: '#0a0a0a' }}
                />
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex items-center gap-4">
              {/* Sort Options */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-300">Sort by:</span>
                <div className="relative" ref={sortDropdownRef}>
                  <button
                    onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                    className="flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white text-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer min-w-[140px]"
                  >
                    <span>{sortOptions.find(opt => opt.id === sortBy)?.name}</span>
                    <ChevronDown 
                      size={16} 
                      className={`text-gray-400 transition-transform duration-200 ${isSortDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  {isSortDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                      {sortOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleSortChange(option.id)}
                          className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 flex items-center justify-between ${
                            option.id === sortBy
                              ? 'bg-white/10 text-white'
                              : 'text-gray-300 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span>{option.name}</span>
                          {option.id === sortBy && (
                            <Check size={16} className="text-white" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-full mx-auto px-4 py-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white">Your Favorites</h2>
          <p className="text-gray-400 text-sm">
            {isConnected ? (
              `${getCurrentData().length} ${activeTab === 'pieces' ? 'pieces' : activeTab === 'capsules' ? 'capsules' : 'wallets'} saved`
            ) : (
              'Connect your wallet to view your favorite pieces'
            )}
          </p>
        </div>

        {/* Show message when wallet not connected */}
        {!isConnected && activeTab === 'pieces' ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">💳</div>
            <h3 className="text-2xl font-bold text-white mb-2">Connect Your Wallet</h3>
            <p className="text-gray-400 mb-6">Connect your wallet to view and manage your favorite pieces</p>
            <button 
              onClick={connectWallet}
              className="px-6 py-3 bg-white hover:bg-gray-200 text-black rounded-xl font-semibold transition-all duration-200"
            >
              Connect Wallet
            </button>
          </div>
        ) : getCurrentData().length === 0 && isConnected ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">❤️</div>
            <h3 className="text-2xl font-bold text-white mb-2">No Favorites Yet</h3>
            <p className="text-gray-400 mb-6">Start exploring pieces and add them to your favorites</p>
            <button 
              onClick={() => navigate('/explore')}
              className="px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl font-semibold transition-all duration-200 shadow-lg"
            >
              Explore Pieces
            </button>
          </div>
        ) : (
          <div className={`grid ${
            activeTab === 'pieces' 
              ? 'gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8' 
              : activeTab === 'capsules'
              ? 'gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              : 'gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {getCurrentData().map((item) => {
              // For pieces, check ownership
              if (activeTab === 'pieces') {
                // Check if current user owns this piece
                const isOwner = isConnected && publicKey && 
                  item?.holder_wallet === publicKey.toString();
                
                // Determine if piece is owned by creator (primary sale)
                const creatorWallet = item?.creator_wallet || 
                                    item?.creators?.wallet ||
                                    item?.originalCreator?.wallet;
                const isOwnedByCreator = item?.holder_wallet === creatorWallet;
                
                // Show buy button only if user doesn't own it AND (piece is owned by creator OR listed for resale)
                const canBuy = !isOwner && (isOwnedByCreator || item?.listing_status === 'listed');
                
                // Show trade button only if user doesn't own it
                const canTrade = !isOwner;
                
                // Check if any piece in the group is favorited
                // Ensure consistent ID comparison (convert to string)
                const isAnyPieceFavorited = item._isGroup 
                  ? item._groupPieces?.some(p => localFavoritedPieces.has(String(p.id)))
                  : localFavoritedPieces.has(String(item.id));
                
                return (
                  <FavoritePieceCard
                    key={item.id}
                    item={item}
                    onClick={openPieceDetailModal}
                    onBuy={handleBuyNow}
                    onTrade={handleRequestTrade}
                    onFavoriteToggle={handleToggleFavorite}
                    isFavorited={isAnyPieceFavorited}
                    solPrice={solPrice}
                    showBuyButton={canBuy}
                    showTradeButton={canTrade}
                    buyButtonText="Buy now"
                    tradeButtonText="Trade"
                  />
                );
              }
              
              return activeTab === 'wallets' ? (
              // Wallet Card Design - Clean and Minimal
              <div 
                key={item.id}
                onClick={() => handleViewProfile(item)}
                className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 cursor-pointer"
              >
                {/* Profile Picture and Username */}
                <div className="p-4 flex items-center gap-4">
                  <div className="relative">
                    <img 
                      src={item.avatar || `https://ui-avatars.com/api/?name=${item.username}&background=333&color=fff&size=64`} 
                      alt={item.username}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-bold text-lg">
                        {item.username}
                      </h3>
                      {item.verified && (
                        <VerifiedBadge 
                          size="md" 
                          verificationType={item.verificationType || 'sales_milestone'}
                        />
                      )}
                    </div>
                    <p className="text-gray-300 text-sm line-clamp-2">
                      {item.bio || 'Digital fashion creator'}
                    </p>
                  </div>
                </div>
                
                {/* Wallet Address */}
                <div className="px-4 pb-4">
                  <div className="text-xs text-gray-500 font-mono">
                    {item.walletAddress?.slice(0, 8)}...{item.walletAddress?.slice(-8)}
                  </div>
                </div>
                
                {/* Heart Icon */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleWalletFavorite(item.id);
                  }}
                  className="absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                >
                  <Heart 
                    size={16} 
                    className={`${
                      isWalletFavorited(item.id) 
                        ? 'text-red-500 fill-red-500' 
                        : 'text-white/70 hover:text-red-500'
                    } transition-all duration-300`} 
                  />
                </button>
              </div>
            ) : (
              // Capsule Card Design - Match Capsules page
              <div 
                key={item.id} 
                className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 aspect-[420/320]"
              >
                <div 
                  onClick={() => handleViewCapsule(item)}
                  className="cursor-pointer group relative h-full flex flex-col"
                >
                  {/* Banner Image - Top half */}
                  <div className="relative h-1/2 bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                    {item.banner ? (
                      <img 
                        src={item.banner} 
                        alt={item.title || item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.title || item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img 
                        src="https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=300&h=200&fit=crop" 
                        alt={item.title || item.name}
                        className="w-full h-full object-cover"
                      />
                    )}

                    {/* Heart Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(item);
                      }}
                      className="absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                    >
                      <Heart 
                        size={16} 
                        className={`${
                          localFavoritedCapsules.has(item.id) 
                            ? 'text-red-500 fill-red-500' 
                            : 'text-white/70 hover:text-red-500'
                        } transition-all duration-300`} 
                      />
                    </button>
                  </div>

                  {/* Bottom Section with Info - Bottom half */}
                  <div className="h-1/2 p-3 flex flex-col justify-between">
                    <div className="space-y-1">
                      {/* Title */}
                      <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
                        {item.title || item.name}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed">
                        {item.description}
                      </p>

                      {/* Pieces Count */}
                      <div className="text-gray-400 text-xs">
                        • {item.pieceCount || item.piece_count || 0} {(item.pieceCount || item.piece_count || 0) === 1 ? 'piece' : 'pieces'}
                      </div>
                    </div>

                    <div className="space-y-1">
                      {/* Avg Price Row */}
                      {(() => {
                        const price = parseFloat(item.avgPrice || item.price || 0);
                        return price > 0 ? (
                          <div className="flex items-center justify-between">
                            <div className="text-gray-400 text-xs">Avg Price</div>
                            <div className="text-right">
                              <div className="text-green-400 font-semibold text-sm">
                                {price < 0.1 ? price.toFixed(3) : price.toFixed(1)} SOL
                              </div>
                              <div className="text-gray-500 text-xs">
                                ~${(price * solPrice).toFixed(2)} USD
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {/* Drop Time */}
                      {(item.dropDate || item.createdAt) && (
                        <div className="text-gray-500 text-xs">
                          Dropped {new Date(item.dropDate || item.createdAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Grouped Pieces Modal */}
      {groupedPiecesModal.isOpen && (
        <GroupedPiecesModal
          isOpen={groupedPiecesModal.isOpen}
          onClose={() => setGroupedPiecesModal({ isOpen: false, groupedCard: null })}
          groupedCard={groupedPiecesModal.groupedCard}
          onBuyNow={handleBuyNow}
          onMakeOffer={handleMakeOffer}
          onRequestTrade={handleRequestTrade}
          solPrice={solPrice || 195.84}
          wallet={wallet}
          publicKey={publicKey}
          isConnected={isConnected}
        />
      )}

      {/* Piece Detail Modal */}
      {pieceDetailModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closePieceDetailModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
            style={{ overflow: 'visible' }}
          >
            <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
               {/* Left side - Thumbnail/3D Model */}
               <div className="w-1/2 p-6 overflow-y-auto">
                {/* 3D Model Toggle Buttons */}
                {(() => {
                  const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                  const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
                  
                  if (has3DModel) {
                    return (
                      <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                    <button
                      onClick={() => setModalViewMode('3d')}
                      className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                        modalViewMode === '3d'
                          ? 'text-white bg-[#2a2a2a] shadow-lg' 
                          : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                      }`}
                    >
                      3D Model
                    </button>
                    <button
                      onClick={() => setModalViewMode('thumbnail')}
                      className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                        modalViewMode === 'thumbnail'
                          ? 'text-white bg-[#2a2a2a] shadow-lg' 
                          : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                      }`}
                    >
                      Thumbnail
                    </button>
                  </div>
                    );
                  }
                  return null;
                })()}

                <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square bg-[#1a1a1a]">
                  {(() => {
                    const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                    
                    if (modalViewMode === '3d') {
                      // Try to get bucket/key info from the piece data
                      const modelBucket = pieceDetailModal.item?.preview_bucket || pieceDetailModal.item?.model_bucket;
                      const modelKey = pieceDetailModal.item?.preview_key || pieceDetailModal.item?.model_key;
                      
                      // Fallback to legacy URL if bucket/key not available
                      const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                      
                      if (modelBucket && modelKey) {
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            modelBucket={modelBucket}
                            modelKey={modelKey}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else if (fallbackSrc) {
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            src={fallbackSrc}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else {
                        return (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">⚠️</div>
                              <div className="text-xl font-semibold mb-2">No 3D model available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Show thumbnail image
                      return pieceDetailModal.item?.thumbnailUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnailUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            console.log('Modal thumbnail load error for:', pieceDetailModal.item.name, 'URL:', pieceDetailModal.item.thumbnailUrl);
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : pieceDetailModal.item?.thumbnail?.dataUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnail.dataUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      );
                    }
                  })()}
                </div>

                {/* Creator and Holder Section */}
                <div className="space-y-4 mb-6">
                  {/* Original Creator */}
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Original Creator
                    </div>
                    <button 
                      onClick={() => handleAccountClick(pieceDetailModal.item)}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url) ? (
                          <img
                            src={pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url}
                            alt={pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator'}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {(pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator').charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                            {pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Original Creator'}
                          </div>
                          {(pieceDetailModal.item?.originalCreator?.verified || pieceDetailModal.item?.creator?.verified) && (
                            <VerifiedBadge 
                              size="sm"
                              verificationType={pieceDetailModal.item?.originalCreator?.verificationType || pieceDetailModal.item?.creator?.verificationType || 'none'}
                            />
                          )}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {pieceDetailModal.item?.originalCreator?.piecesCount || pieceDetailModal.item?.creator?.piecesCount || '0'} pieces created
                        </div>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </button>

                    {/* Creator Royalty - Outside button */}
                    {pieceDetailModal.item?.royalty_bps !== undefined && pieceDetailModal.item?.royalty_bps !== null && (
                      <div className="mt-3 px-1">
                        <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                          {(pieceDetailModal.item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                        </span>
                      </div>
                    )}

                    {/* Social Links */}
                    {(() => {
                      // Prioritize capsule social links if piece belongs to a capsule, otherwise use piece social links
                      const socialLinks = pieceCapsuleInfo 
                        ? {
                            website: pieceCapsuleInfo.website,
                            twitter: pieceCapsuleInfo.twitter,
                            instagram: pieceCapsuleInfo.instagram,
                            discord: pieceCapsuleInfo.discord
                          }
                        : (pieceDetailModal.item?.social_links || {});
                      const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                      
                      // DEBUG: Log social links data
                      console.log('🔍 [FAVORITES] Social Links Debug:', {
                        pieceId: pieceDetailModal.item?.id,
                        pieceName: pieceDetailModal.item?.name,
                        pieceCapsuleInfo: pieceCapsuleInfo,
                        usingCapsuleLinks: !!pieceCapsuleInfo,
                        social_links: socialLinks,
                        hasSocialLinks
                      });
                      
                      if (!hasSocialLinks) return null;
                      
                      return (
                        <div className="flex items-center gap-2 mt-3 px-1">
                      {socialLinks.website && (
                        <a
                          href={socialLinks.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Website"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.twitter && (
                        <a
                          href={socialLinks.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="X (Twitter)"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.instagram && (
                        <a
                          href={socialLinks.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Instagram"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.discord && (
                        <a
                          href={socialLinks.discord}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Discord"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                          </svg>
                        </a>
                      )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {pieceDetailModal.item?.name}
                      </h2>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(pieceDetailModal.item.id);
                        }}
                        className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        title={isFavorited(pieceDetailModal.item.id) ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Heart 
                          size={20} 
                          className={`transition-all duration-300 ${
                            isFavorited(pieceDetailModal.item.id)
                              ? 'text-red-500 fill-red-500'
                              : 'text-gray-400 hover:text-red-500'
                          }`} 
                        />
                      </button>
                    </div>
                    {/* Custom Tags */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {pieceDetailModal.item?.tags && pieceDetailModal.item.tags.length > 0 ? (
                        pieceDetailModal.item.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      activityPanelOpen 
                        ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                        : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                    }`}
                  >
                    Activity
                  </button>
                </div>

                <div className="space-y-5">
                  {/* Category and Color */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-gray-300 text-sm font-medium">
                          {getCategoryDisplayName(pieceDetailModal.item?.category) || 'Uncategorized'}
                        </span>
                      </div>
                      {pieceDetailModal.item?.color && (
                        <div className="text-gray-300 text-sm">
                          <span className="text-gray-500">Color: </span>
                          <span className="capitalize font-medium">{pieceDetailModal.item.color}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {pieceDetailModal.item?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Price</span>
                        <div className="text-green-400 font-bold text-xl">
                          {(() => {
                            const item = pieceDetailModal.item;
                            const isOwnedByCurrentUser = publicKey && item?.holder_wallet === publicKey.toString();
                            const isListed = item?.listing_status === 'listed';
                            
                            let displayPrice;
                            // If user owns this piece
                            if (isOwnedByCurrentUser) {
                              // If they've listed it, show THEIR listing price
                              if (isListed && item?.listing_price) {
                                displayPrice = item.listing_price;
                              }
                              // If not listed, show purchase price
                              else if (!isListed && item?.last_sale_price_lamports) {
                                displayPrice = item.last_sale_price_lamports / 1_000_000_000;
                              }
                              else {
                                displayPrice = item?.price || 0;
                              }
                            } else {
                              // For pieces not owned by user, show lowest available price
                              displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                            }
                            
                            // Format price to remove trailing zeros
                            const formatted = typeof displayPrice === 'number' 
                              ? displayPrice.toFixed(4).replace(/\.?0+$/, '')
                              : displayPrice;
                            return formatted;
                          })()} SOL
                        </div>
                      </div>
                      <div className="text-right text-gray-500 text-sm">
                        ≈ ${(() => {
                          const item = pieceDetailModal.item;
                          const isOwnedByCurrentUser = publicKey && item?.holder_wallet === publicKey.toString();
                          const isListed = item?.listing_status === 'listed';
                          
                          let displayPrice;
                          if (isOwnedByCurrentUser) {
                            if (isListed && item?.listing_price) {
                              displayPrice = item.listing_price;
                            } else if (!isListed && item?.last_sale_price_lamports) {
                              displayPrice = item.last_sale_price_lamports / 1_000_000_000;
                            } else {
                              displayPrice = item?.price || 0;
                            }
                          } else {
                            displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                          }
                          return ((displayPrice || 0) * solPrice).toFixed(2);
                        })()} USD
                      </div>
                    </div>
                  </div>

                  {/* Scarcity */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                    <div className="text-white font-semibold text-lg">
                      {pieceDetailModal.item?.quantity || 1}
                    </div>
                  </div>

                  {/* Dropped Date */}
                  <div className="text-gray-500 text-sm pt-2">
                    Dropped {pieceDetailModal.item?.createdAt ? new Date(pieceDetailModal.item.createdAt).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Recently'}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4 pb-2">
                    {/* PRIMARY & SECONDARY MARKETPLACE BUTTONS */}
                    {(() => {
                      const isOwner = isConnected && publicKey && 
                        pieceDetailModal.item?.holder_wallet === publicKey.toString();
                      
                      const isSeller = isConnected && publicKey && 
                        activeListing?.seller_wallet === publicKey.toString();
                      
                      // Check if piece is owned by the original creator (primary sale)
                      const creatorWallet = pieceDetailModal.item?.creator_wallet || 
                                           pieceDetailModal.item?.creators?.wallet ||
                                           pieceDetailModal.item?.originalCreator?.wallet;
                      const isOwnedByCreator = pieceDetailModal.item?.holder_wallet === creatorWallet;
                      
                      const listEligibility = canListPiece(wallet, pieceDetailModal.item);
                      const purchaseEligibility = canPurchaseListing(wallet, activeListing, pieceDetailModal.item);
                      
                      return (
                        <>
                          {/* OWNERSHIP BADGE: Show when user owns the piece */}
                          {isOwner && (
                            <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-xl text-center border border-green-600/30 mb-2">
                              <span className="text-sm font-medium">✓ You own this piece</span>
                            </div>
                          )}
                          
                          {/* PRIMARY SALE: Buy directly from creator (no listing needed) */}
                          {!isOwner && isOwnedByCreator && 
                           pieceDetailModal.item?.mint_address && 
                           !activeListing &&
                           pieceDetailModal.item?.listing_status !== 'sold' && (
                            <button
                              onClick={() => handleBuyNow(pieceDetailModal.item)}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25"
                            >
                              Buy Now - {(pieceDetailModal.item?.listing_price || pieceDetailModal.item?.price || 0).toFixed(4)} SOL
                            </button>
                          )}
                          
                          {/* SECONDARY SALE: Owner can list for resale (including relisting after purchase) */}
                          {isOwner && !activeListing && pieceDetailModal.item?.listing_status !== 'listed' && pieceDetailModal.item?.listing_status !== 'sold' && (
                            <button
                              onClick={() => navigate('/listings')}
                              className="w-full px-4 py-3.5 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a]"
                            >
                              List for Sale
                            </button>
                          )}
                          
                          {/* SECONDARY SALE: Seller can cancel listing */}
                          {isSeller && activeListing && activeListing.status === 'active' && (
                            <button
                              onClick={handleCancelListing}
                              disabled={listingLoading}
                              className="w-full px-4 py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg disabled:opacity-50"
                            >
                              {listingLoading ? 'Canceling...' : '❌ Cancel Listing'}
                            </button>
                          )}
                          
                          {/* SECONDARY SALE: Buyer can purchase listed piece */}
                          {!isOwner && activeListing && activeListing.status === 'active' && 
                           pieceDetailModal.item?.listing_status === 'listed' && (
                            <button
                              onClick={handlePurchaseListing}
                              disabled={listingLoading || !purchaseEligibility.canPurchase}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                            >
                              {listingLoading ? 'Processing...' : `Buy Now - ${activeListing.price_sol} SOL`}
                            </button>
                          )}
                          
                          {/* FALLBACK: Show buy button for listed pieces even if activeListing hasn't loaded yet */}
                          {!isOwner && !isOwnedByCreator && !activeListing &&
                           pieceDetailModal.item?.listing_status === 'listed' &&
                           pieceDetailModal.item?.listing_price && (
                            <button
                              onClick={() => handleBuyNow(pieceDetailModal.item)}
                              disabled={listingLoading}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                            >
                              {listingLoading ? 'Loading...' : `Buy Now - ${(pieceDetailModal.item?.listing_price || 0).toFixed(4)} SOL`}
                            </button>
                          )}
                          
                          {/* Show sold status only if viewer is NOT the current owner */}
                          {pieceDetailModal.item?.listing_status === 'sold' && !isOwner && (
                            <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center font-semibold text-sm">
                              ✅ Sold
                            </div>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* Original buttons - Make Offer and Request Trade */}
                    {(() => {
                      const isOwner = isConnected && publicKey && 
                        pieceDetailModal.item?.holder_wallet === publicKey.toString();
                      
                      // Don't show any action buttons if user owns the piece
                      if (isOwner) {
                        return null;
                      }
                      
                      return (
                        <div className="flex gap-3">
                          <button 
                            onClick={() => handleMakeOffer(pieceDetailModal.item)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a]"
                          >
                            Make Offer
                          </button>
                          <button 
                            onClick={() => handleRequestTrade(pieceDetailModal.item)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a] flex items-center justify-center gap-2"
                          >
                            <ArrowLeftRight size={16} />
                            Request Trade
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                  {/* View Entire Capsule Button - Only show if piece belongs to a capsule */}
                  {(() => {
                    console.log('🔍 Rendering capsule button check:', { pieceCapsuleInfo, hasCapsule: !!pieceCapsuleInfo });
                    return pieceCapsuleInfo && (
                      <div className="pt-2">
                        <button 
                          onClick={() => handleViewCapsule(pieceCapsuleInfo)}
                          className="w-full px-4 py-2.5 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#3a3a3a] hover:border-[#4a4a4a] text-gray-300 hover:text-white rounded-lg transition-all duration-200 font-medium text-sm flex items-center justify-center gap-2"
                        >
                          <Package size={14} />
                          View entire capsule
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Activity Side Panel - slides out from behind modal on desktop, overlay on mobile */}
            <div 
              className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
              style={{ 
                transform: activityPanelOpen ? 'translateX(0)' : (window.innerWidth < 768 ? 'translateX(-100%)' : 'translateX(-100%)'),
                zIndex: activityPanelOpen ? 50 : 0,
                opacity: activityPanelOpen ? 1 : 0,
                visibility: activityPanelOpen ? 'visible' : 'hidden',
                pointerEvents: activityPanelOpen ? 'auto' : 'none'
              }}
            >
              <div className="h-full flex flex-col p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                  <h3 className="text-base font-bold text-white">Activity</h3>
                  <button
                    onClick={() => setActivityPanelOpen(false)}
                    className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                    title="Close Activity Panel"
                  >
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <PieceActivityAndHolders
                    pieceId={pieceDetailModal.item?.id}
                    pieceName={pieceDetailModal.item?.name}
                    basePrice={parseFloat(pieceDetailModal.item?.price) || 0}
                    solPrice={solPrice || 150}
                    totalQuantity={pieceDetailModal.item?.total_quantity || pieceDetailModal.item?.quantity || 1}
                    groupedPieceIds={currentPieceGroupIds}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buy Modal */}
      {buyModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Purchase Item</h3>
                <p className="text-gray-400 text-sm">Complete your purchase</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{buyModal.item?.name}</h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Price</span>
                  <div className="text-right">
                    <div className="text-green-400 font-bold text-lg">{buyModal.item?.price} SOL</div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${(parseFloat(buyModal.item?.price || '0') * solPrice).toFixed(2)} USD
                    </div>
                  </div>
                </div>
              </div>

              {/* Wallet Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Connected Wallet</span>
                    <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-green-400" />
                      <span className="text-green-400 text-sm font-medium">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </span>
                    </div>
                  </div>
                  {isConnected && publicKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                  )}
                  {isConnected && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-300 text-sm">
                You are about to purchase this digital fashion piece. The transaction will be processed on the Solana blockchain.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeBuyModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processPurchase}
                disabled={isProcessing || !isConnected || balance < parseFloat(buyModal.item?.price || '0')}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wallet size={16} />
                    Confirm Purchase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offer Modal */}
      {offerModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Make an Offer</h3>
                <p className="text-gray-400 text-sm">Submit your offer for this piece</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{offerModal.item?.name}</h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">List Price</span>
                  <div className="text-right">
                    <div className="text-white font-bold text-lg">{offerModal.item?.price} SOL</div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${(parseFloat(offerModal.item?.price || '0') * solPrice).toFixed(2)} USD
                    </div>
                  </div>
                </div>
              </div>

              {/* Offer Amount Input */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <label className="block text-white font-medium mb-3">Your Offer Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={offerModal.offerAmount}
                    onChange={(e) => setOfferModal(prev => ({ ...prev, offerAmount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                    SOL
                  </div>
                </div>
                {offerModal.offerAmount && !isNaN(parseFloat(offerModal.offerAmount)) && (
                  <div className="mt-2 text-right text-gray-500 text-sm">
                    ≈ ${(parseFloat(offerModal.offerAmount) * solPrice).toFixed(2)} USD
                  </div>
                )}
              </div>

              {/* Wallet Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Connected Wallet</span>
                    <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-green-400" />
                      <span className="text-green-400 text-sm font-medium">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </span>
                    </div>
                  </div>
                  {isConnected && publicKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                  )}
                  {isConnected && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-300 text-sm">
                Your offer will be locked in escrow until the seller accepts or rejects it. You can cancel your offer at any time.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeOfferModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processOffer}
                disabled={isProcessing || !isConnected || !offerModal.offerAmount || isNaN(parseFloat(offerModal.offerAmount)) || parseFloat(offerModal.offerAmount) <= 0 || balance < parseFloat(offerModal.offerAmount || '0')}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Package size={16} />
                    Submit Offer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Trade Modal */}
      {requestTradeModal.isOpen && (
        <RequestTradeModal
          isOpen={requestTradeModal.isOpen}
          onClose={() => setRequestTradeModal({ isOpen: false, item: null })}
          targetPiece={requestTradeModal.item}
          onRequestTrade={(tradeRequest) => {
            console.log('Trade request submitted:', tradeRequest);
            setRequestTradeModal({ isOpen: false, item: null });
          }}
          onSuccess={(message) => {
            console.log('Trade request sent successfully');
            showNotification(message || 'Trade request sent successfully! 🎉', 'success');
          }}
        />
      )}

      {/* Purchase Confirmation Modal for Listings */}
      {showPurchaseConfirmModal && pieceDetailModal.item && activeListing && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => !listingLoading && setShowPurchaseConfirmModal(false)}>
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Purchase Item</h3>
                <p className="text-gray-400 text-sm">Complete your purchase</p>
              </div>
            </div>

            {/* Item Name */}
            <h4 className="text-white font-medium text-lg mb-4">{pieceDetailModal.item.name}</h4>

            {/* Price Breakdown */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-blue-400">Price</span>
                <div className="text-right">
                  <div className="text-green-400 font-bold text-lg">{activeListing.price_sol?.toFixed(4)} SOL</div>
                  <div className="text-gray-500 text-sm">
                    ≈ ${((activeListing.price_sol || 0) * solPrice).toFixed(2)} USD
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-400">Sale Fee (2.5%)</span>
                <div className="text-right">
                  <div className="text-orange-400 font-medium">
                    {((activeListing.price_sol || 0) * 0.025).toFixed(4)} SOL
                  </div>
                  <div className="text-gray-500 text-sm">
                    ≈ ${(((activeListing.price_sol || 0) * 0.025) * solPrice).toFixed(2)} USD
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mb-6">
                <span className="text-gray-400">Seller Receives</span>
                <div className="text-right">
                  <div className="text-white font-medium">
                    {((activeListing.price_sol || 0) * 0.975).toFixed(4)} SOL
                  </div>
                  <div className="text-gray-500 text-sm">
                    ≈ ${(((activeListing.price_sol || 0) * 0.975) * solPrice).toFixed(2)} USD
                  </div>
                </div>
              </div>
            </div>

            {/* Wallet Information */}
            <div className="mb-6">
              <h4 className="text-white font-medium mb-3">Wallet Information</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Connected Wallet</span>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-400" />
                    <span className="text-green-400 text-sm font-medium">
                      {isConnected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                </div>
                {isConnected && publicKey && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Info Message */}
            <div className="text-gray-300 text-sm mb-6">
              You are about to purchase this digital fashion piece. The transaction will be processed on the Solana blockchain.
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowPurchaseConfirmModal(false)}
                disabled={listingLoading}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmPurchaseListing}
                disabled={listingLoading || !isConnected}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {listingLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wallet size={16} />
                    Confirm Purchase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Connection Modal */}
      <WalletConnectionModal
        isOpen={showWalletModal}
        onClose={closeWalletModal}
        message={walletModalMessage}
      />

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[70] animate-slide-down">
          <div className={`${
            notification.type === 'success' ? 'bg-green-500/20 border-green-500/50' :
            notification.type === 'error' ? 'bg-red-500/20 border-red-500/50' :
            'bg-blue-500/20 border-blue-500/50'
          } border rounded-xl p-4 shadow-xl max-w-md`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                notification.type === 'success' ? 'bg-green-500/20' :
                notification.type === 'error' ? 'bg-red-500/20' :
                'bg-blue-500/20'
              }`}>
                {notification.type === 'success' ? (
                  <CheckCircle size={20} className="text-green-400" />
                ) : notification.type === 'error' ? (
                  <AlertCircle size={20} className="text-red-400" />
                ) : (
                  <AlertCircle size={20} className="text-blue-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification({ ...notification, show: false })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </WalletRequired>
  );
};

export default Favorites; 